import type { LogsEvent } from "@datadog/browser-logs";
import type { RumEvent } from "@datadog/browser-rum";

// Box Preview (loaded from cdn01.boxcdn.net by Box's ContentExplorer) bundles
// pdf.js's web viewer, whose `scrollIntoView` calls
// `console.error("offsetParent is not set -- cannot scroll")` when the page it
// is asked to scroll has no layout. ENG-2354: 91% of these fire on the view
// after the patient E-Documents tab — someone left with a PDF preview open and
// Box's teardown scrolled detached nodes — almost always as one pair per view.
// The other 9% fire while still on E-Documents, so nothing is thrown away:
// Logs gets it as a `warn` (off the error monitor, rate still visible) and RUM
// keeps it, tagged so it can be filtered out.
export const BOX_PREVIEW_DETACHED_SCROLL_MESSAGE =
  "offsetParent is not set -- cannot scroll";

export const BOX_PREVIEW_DETACHED_SCROLL_TAG = "box_preview_detached_scroll";

const BOX_CDN_DOMAIN = "boxcdn.net";

// pdf.js's viewer file inside Box Preview, the code that logs the message.
const PDF_VIEWER_FILE = /\/pdf_viewer[^/]*\.js$/;

type StackFrame = { host: string; path: string };

function parseFrame(line: string): StackFrame | null {
  const url = line.split(" @ ")[1];
  if (!url) return null;
  try {
    const { hostname, pathname } = new URL(url);
    // Frames end in `:line:col`, which URL keeps on the path.
    return { host: hostname, path: pathname.replace(/(:\d+)+$/, "") };
  } catch {
    return null;
  }
}

const isBoxCdnHost = (host: string) =>
  host === BOX_CDN_DOMAIN || host.endsWith(`.${BOX_CDN_DOMAIN}`);

/**
 * True when the first Box CDN frame in an SDK handling stack
 * (`HandlingStack: console error\n  at fn @ https://host/file.js:1:2`) is
 * pdf.js's viewer — i.e. pdf.js logged it. Frames above it are ignored on
 * purpose: they are console wrappers, whatever the browser names them —
 * `WordProcessorField` while a TinyMCE editor is mounted (`console.error` in
 * Chrome) and the Userback widget (`<anonymous>` in Safari) both sit there in
 * production. App code that logs from inside a Box explorer callback has
 * `explorer.js` beneath it, not the viewer, so it is still reported.
 */
function isLoggedByBoxPdfViewer(handlingStack: string | undefined): boolean {
  const firstBoxFrame = handlingStack
    ?.split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("at "))
    .map(parseFrame)
    .find((frame) => frame !== null && isBoxCdnHost(frame.host));
  return Boolean(firstBoxFrame && PDF_VIEWER_FILE.test(firstBoxFrame.path));
}

/**
 * True for Box Preview's detached-scroll console error: a console log with
 * the exact message, logged by Box's bundled pdf.js viewer. The same text
 * from anywhere else does not match.
 */
export function isBoxPreviewDetachedScroll(
  source: string | undefined,
  message: string | undefined,
  handlingStack: string | undefined
): boolean {
  return (
    source === "console" &&
    message === BOX_PREVIEW_DETACHED_SCROLL_MESSAGE &&
    isLoggedByBoxPdfViewer(handlingStack)
  );
}

/**
 * Logs `beforeSend`: turns this error into a tagged `warn` in place, and
 * removes its `error` block so nothing keyed on `@error.*` still counts it
 * (the ResizeObserver sampler does the same).
 */
export function downgradeBoxPreviewDetachedScrollLog(
  log: LogsEvent,
  handlingStack: string | undefined
): void {
  if (!isBoxPreviewDetachedScroll(log.origin, log.message, handlingStack)) {
    return;
  }
  log.status = "warn";
  log[BOX_PREVIEW_DETACHED_SCROLL_TAG] = true;
  delete log.error;
}

/** RUM `beforeSend`: tags this error in its context; the event is kept. */
export function tagBoxPreviewDetachedScrollRumError(event: RumEvent): void {
  if (
    event.type !== "error" ||
    !isBoxPreviewDetachedScroll(
      event.error.source,
      event.error.message,
      event.error.handling_stack
    )
  ) {
    return;
  }
  event.context = { ...event.context, [BOX_PREVIEW_DETACHED_SCROLL_TAG]: true };
}
